import { Archive, BookOpenText, CheckCircle2, Download, FilePlus2, Printer, Send, ShieldCheck, Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { lireDocumentProcedure, signalerLectureProcedure, telechargerDocumentProcedure, useActionsProcedures, useProcedure } from '@/api/hooks';
import { LIBELLE_STATUT_PROCEDURE, type DetailProcedure, type LectureProcedure, type StatutConsultation, type StatutProcedure, type VersionProcedure } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { StatutConsultationBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr } from '@/lib/utils';

const VARIANTE_STATUT: Record<StatutProcedure, 'default' | 'secondary' | 'destructive' | 'outline'> = { BROUILLON: 'outline', EN_VALIDATION: 'secondary', PUBLIEE: 'default', ARCHIVEE: 'outline' };
export const StatutProcedureBadge = ({ statut }: { statut: StatutProcedure }) => <Badge variant={VARIANTE_STATUT[statut]}>{LIBELLE_STATUT_PROCEDURE[statut]}</Badge>;
const message = (e: unknown) => (e instanceof Error ? e.message : 'Action impossible.');
const duree = (s: number) => (s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`);

/**
 * Lecteur intégré (§ 6.2) : le document est lu en mémoire avec la session et affiché dans la page. À l'affichage, le
 * lecteur ouvre une session de lecture ; après le délai paramétré, il pointe la durée et l'API enregistre « Lu ».
 * Le temps passé est pointé à la fermeture ; l'affichage d'une version archivée ne change rien au statut.
 */
function Lecteur({ procedureId, version, concerne, impression, telechargement, onLecture }: { procedureId: string; version: VersionProcedure; concerne: boolean; impression: boolean; telechargement: boolean; onLecture: (r: LectureProcedure) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [restant, setRestant] = useState<number | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  const cadre = useRef<HTMLIFrameElement | null>(null);
  const dernierPointage = useRef(0);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trace = version.enVigueur && concerne;

  useEffect(() => {
    let actif = true;
    let objet: string | null = null;
    setUrl(null);
    setErreur(null);
    lireDocumentProcedure(procedureId, version.id)
      .then((b) => {
        if (!actif) return;
        objet = URL.createObjectURL(b);
        setUrl(objet);
      })
      .catch((e: unknown) => actif && setErreur(message(e)));
    return () => {
      actif = false;
      if (objet) URL.revokeObjectURL(objet);
    };
  }, [procedureId, version.id]);

  // Pointage du temps passé à la fermeture du lecteur (ou au changement de version).
  useEffect(() => {
    return () => {
      if (minuteur.current) clearTimeout(minuteur.current);
      if (trace && dernierPointage.current) {
        const d = Math.round((Date.now() - dernierPointage.current) / 1000);
        if (d > 0) void signalerLectureProcedure(procedureId, d).catch(() => undefined);
      }
    };
  }, [procedureId, version.id, trace]);

  useEffect(() => {
    if (restant === null || restant <= 0) return;
    const t = setInterval(() => setRestant((r) => (r === null ? null : Math.max(0, r - 1))), 1000);
    return () => clearInterval(t);
  }, [restant]);

  const affichage = () => {
    if (!trace || dernierPointage.current) return;
    dernierPointage.current = Date.now();
    signalerLectureProcedure(procedureId)
      .then((r) => {
        if (r.statut === 'A_CONSULTER' || r.statut === 'A_RELIRE') {
          setRestant(r.delaiSecondes);
          minuteur.current = setTimeout(async () => {
            try {
              const r2 = await signalerLectureProcedure(procedureId, Math.round((Date.now() - dernierPointage.current) / 1000));
              dernierPointage.current = Date.now();
              setRestant(0);
              setEnregistre(true);
              onLecture(r2);
            } catch (e) {
              toast.error(message(e));
            }
          }, r.delaiSecondes * 1000 + 300);
        } else {
          setRestant(0);
          setEnregistre(true);
        }
      })
      .catch((e: unknown) => toast.error(message(e)));
  };

  const imprimer = () => {
    try {
      const fenetre = cadre.current?.contentWindow;
      if (fenetre) fenetre.print();
      else if (url) window.open(url, '_blank');
    } catch {
      if (url) window.open(url, '_blank');
    }
  };
  const telecharger = async () => {
    try {
      await telechargerDocumentProcedure(procedureId, version.document?.nomFichier ?? 'procedure.pdf', version.id);
    } catch (e) {
      toast.error(message(e));
    }
  };

  return (
    <Card data-testid="lecteur-procedure">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <BookOpenText className="size-5" /> Lecteur intégré · version {version.numero}
            {!version.enVigueur && <Badge variant="outline">{version.statut === 'ARCHIVEE' ? 'version archivée' : version.libelleStatut.toLowerCase()}</Badge>}
          </span>
          <span className="flex gap-2">
            {telechargement && (
              <Button type="button" variant="outline" size="sm" onClick={telecharger}>
                <Download className="size-4" /> Télécharger
              </Button>
            )}
            {impression && (
              <Button type="button" variant="outline" size="sm" onClick={imprimer} disabled={!url}>
                <Printer className="size-4" /> Imprimer
              </Button>
            )}
          </span>
        </CardTitle>
        <CardDescription>
          {!trace
            ? version.enVigueur
              ? 'Affichage sans trace : cette procédure ne vous est pas applicable ou vous la consultez au titre de vos droits.'
              : 'Version archivée ou en préparation : sa consultation ne modifie pas votre statut.'
            : enregistre
              ? 'Consultation enregistrée sous votre compte (statut « Lu »).'
              : restant !== null && restant > 0
                ? `Lecture en cours : le statut « Lu » sera enregistré dans ${restant} s d'affichage.`
                : 'Le statut « Lu » est enregistré après l’affichage effectif du document pendant le délai minimal.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {erreur ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-red-700">{erreur}</div>
        ) : !url ? (
          <Chargement />
        ) : (
          <iframe ref={cadre} src={url} title={`Procédure, version ${version.numero}`} className="h-[72vh] w-full rounded-md border bg-white" onLoad={affichage} />
        )}
      </CardContent>
    </Card>
  );
}

/** Préparation d'une version en brouillon (gestionnaires) : notes, échéance, entrée en vigueur, document principal PDF et annexes, soumission. */
function PreparationVersion({ d, version }: { d: DetailProcedure; version: VersionProcedure }) {
  const actions = useActionsProcedures();
  const [notes, setNotes] = useState(version.notes ?? '');
  const [echeance, setEcheance] = useState(version.echeanceConsultationJours ? String(version.echeanceConsultationJours) : '');
  const [vigueur, setVigueur] = useState(version.dateEntreeVigueur ?? '');
  const enregistrer = async () => {
    try {
      await actions.modifierVersion.mutateAsync({ id: d.procedure.id, versionId: version.id, notes: notes.trim() || null, echeanceConsultationJours: echeance ? Number(echeance) : null, dateEntreeVigueur: vigueur || null });
      toast.success('Version enregistrée.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  const soumettre = async () => {
    try {
      const r = await actions.soumettre.mutateAsync({ id: d.procedure.id, versionId: version.id });
      const v = r.versions.find((x) => x.id === version.id);
      toast.success(`Version ${version.numero} soumise${v?.validateursAttendus.length ? ` à ${v.validateursAttendus.join(', ')}` : ''}.`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Card data-testid="preparation-version" className="border-primary/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FilePlus2 className="size-5" /> Préparation de la version {version.numero}
        </CardTitle>
        <CardDescription>
          {version.validations?.renvoi ? `Renvoyée par ${version.validations.renvoi.parNom} le ${dateHeureFr(version.validations.renvoi.le)} : ${version.validations.renvoi.motif}` : 'Joignez le document principal au format PDF (un document Word ou tableur est accepté en annexe), précisez les notes de version et l’échéance de consultation, puis soumettez au directeur de la structure responsable.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1 sm:col-span-3">
            <Label htmlFor="v-notes">Notes de version (résumé des changements)</Label>
            <Textarea id="v-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="v-echeance">Échéance de consultation (jours après publication)</Label>
            <Input id="v-echeance" type="number" min={1} max={365} value={echeance} onChange={(e) => setEcheance(e.target.value)} placeholder="paramètre (30)" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="v-vigueur">Date d'entrée en vigueur</Label>
            <Input id="v-vigueur" type="date" value={vigueur} onChange={(e) => setVigueur(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button type="button" variant="outline" onClick={enregistrer} disabled={actions.modifierVersion.isPending}>
              Enregistrer
            </Button>
          </div>
        </div>
        <PiecesJointes objetType="PROCEDURE" objetId={version.id} typesSuggeres={['DOCUMENT_PRINCIPAL', 'AUTRE']} titre="Document principal et annexes" description="Document principal : PDF obligatoire pour la soumission. Les annexes (Word, Excel, PDF, images) accompagnent la procédure." />
        <div className="flex justify-end">
          <Button type="button" onClick={soumettre} disabled={actions.soumettre.isPending || !version.document?.pdf}>
            <Send className="size-4" /> Soumettre à la validation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Fiche et lecteur (§ 6.8) : fiche, lecteur intégré, annexes, versions, confirmation, historique personnel ; actions de gestion et de validation selon les droits. */
export function ProcedureFiche() {
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useProcedure(id);
  const actions = useActionsProcedures();
  const [versionAffichee, setVersionAffichee] = useState<string | null>(null);
  const [dialogue, setDialogue] = useState<'decision' | 'archiver' | 'relancer' | 'version' | null>(null);
  const [decision, setDecision] = useState<'VALIDER' | 'RENVOYER'>('VALIDER');
  const [texte, setTexte] = useState('');
  const [echeance, setEcheance] = useState('');
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const p = data.procedure;
  const enVigueur = data.versions.find((v) => v.enVigueur) ?? null;
  const brouillon = data.versions.find((v) => v.statut === 'BROUILLON') ?? null;
  const enValidation = data.versions.find((v) => v.statut === 'EN_VALIDATION') ?? null;
  const lisible = data.versions.filter((v) => v.document?.pdf);
  const version = lisible.find((v) => v.id === versionAffichee) ?? enVigueur ?? lisible[0] ?? null;
  const concerne = Boolean(data.personnel?.concerne);
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
      setTexte('');
      setEcheance('');
    } catch (e) {
      toast.error(message(e));
    }
  };
  const confirmer = () => executer(() => actions.confirmer.mutateAsync(p.id), 'Prise de connaissance confirmée.');
  const onLecture = (r: LectureProcedure) => {
    void refetch();
    if (r.statut === 'CONFIRMATION_ATTENDUE') toast.success('Consultation enregistrée : confirmez votre prise de connaissance.');
    else if (r.statut === 'LU') toast.success('Consultation enregistrée (statut « Lu »).');
  };
  return (
    <div className="space-y-4">
      <PageHeader
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {p.reference} · {p.titre} <StatutProcedureBadge statut={p.statut} />
          </span>
        }
        sousTitre={`${p.domaine.libelle} · ${p.structureResponsable.code} ${p.structureResponsable.libelle} · responsable : ${p.responsable.nom}`}
        retour={
          <Link to="/procedures" className="text-sm text-muted-foreground hover:underline">
            ← Bibliothèque des procédures
          </Link>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {data.actions.confirmer && (
              <Button type="button" onClick={confirmer} disabled={actions.confirmer.isPending} data-testid="confirmer-procedure">
                <ShieldCheck className="size-4" /> Je confirme avoir pris connaissance de cette procédure
              </Button>
            )}
            {data.actions.valider && (
              <Button type="button" onClick={() => setDialogue('decision')}>
                <CheckCircle2 className="size-4" /> {data.actions.valider.etape === 'DG' ? 'Viser ou renvoyer' : 'Valider ou renvoyer'}
              </Button>
            )}
            {data.actions.nouvelleVersion && (
              <Button type="button" variant="outline" onClick={() => setDialogue('version')}>
                <FilePlus2 className="size-4" /> Nouvelle version
              </Button>
            )}
            {data.actions.modifier && (
              <Button asChild variant="outline">
                <Link to={`/procedures/gestion?modifier=${p.id}`}>Modifier la fiche</Link>
              </Button>
            )}
            {data.actions.relancer && (
              <Button type="button" variant="outline" onClick={() => setDialogue('relancer')}>
                <Bell className="size-4" /> Relancer
              </Button>
            )}
            {data.actions.suivre && p.statut === 'PUBLIEE' && (
              <Button asChild variant="ghost">
                <Link to={`/procedures/suivi?procedureId=${p.id}`}>Suivi des consultations</Link>
              </Button>
            )}
            {data.actions.archiver && (
              <Button type="button" variant="ghost" onClick={() => setDialogue('archiver')}>
                <Archive className="size-4" /> Archiver
              </Button>
            )}
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {brouillon && data.actions.modifierVersion === brouillon.id && <PreparationVersion key={brouillon.id} d={data} version={brouillon} />}
          {enValidation && (
            <Card className="border-amber-300">
              <CardHeader>
                <CardTitle>Version {enValidation.numero} en validation</CardTitle>
                <CardDescription>
                  Soumise le {dateHeureFr(enValidation.soumiseLe)} · étape attendue : {enValidation.etapeAttendue === 'DG' ? 'visa du Directeur Général' : 'validation du directeur de la structure responsable'}
                  {enValidation.validateursAttendus.length ? ` (${enValidation.validateursAttendus.join(', ')})` : ''}.{enValidation.validations?.directeur ? ` Validée par ${enValidation.validations.directeur.parNom} le ${dateHeureFr(enValidation.validations.directeur.le)}.` : ''}
                </CardDescription>
              </CardHeader>
            </Card>
          )}
          {version ? <Lecteur key={version.id} procedureId={p.id} version={version} concerne={concerne} impression={data.actions.imprimer} telechargement={data.actions.telecharger} onLecture={onLecture} /> : <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Aucun document PDF n'est encore joint à cette procédure.</CardContent></Card>}
          {version && version.annexes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Annexes de la version {version.numero}</CardTitle>
              </CardHeader>
              <CardContent>
                <PiecesJointes objetType="PROCEDURE" objetId={version.id} titre="Annexes" description="Annexes jointes à la version ; le document principal est affiché dans le lecteur." />
              </CardContent>
            </Card>
          )}
          <Card data-testid="versions-procedure">
            <CardHeader>
              <CardTitle>Versions</CardTitle>
              <CardDescription>Une seule version est en vigueur ; les versions remplacées restent consultables, jamais présentées comme en vigueur. Le contenu d'une version publiée ne se remplace pas : toute modification passe par une nouvelle version.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N°</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Publiée le</TableHead>
                    <TableHead>Entrée en vigueur</TableHead>
                    <TableHead>Échéance</TableHead>
                    <TableHead>Validations</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.versions.map((v) => (
                    <TableRow key={v.id} className={cn(v.id === version?.id && 'bg-muted/40')}>
                      <TableCell className="font-medium">
                        v{v.numero} {v.enVigueur && <Badge>en vigueur</Badge>}
                      </TableCell>
                      <TableCell>
                        <StatutProcedureBadge statut={v.statut} />
                      </TableCell>
                      <TableCell className="text-sm">{v.datePublication ? dateFr(v.datePublication) : '—'}</TableCell>
                      <TableCell className="text-sm">{v.dateEntreeVigueur ? dateFr(v.dateEntreeVigueur) : '—'}</TableCell>
                      <TableCell className="text-sm">{v.echeanceDate ? dateFr(v.echeanceDate) : '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.validations?.directeur && <div>Directeur : {v.validations.directeur.parNom}, {dateFr(v.validations.directeur.le)}</div>}
                        {v.validations?.dg && <div>Visa DG : {v.validations.dg.parNom}, {dateFr(v.validations.dg.le)}</div>}
                        {v.validations?.renvoi && v.statut === 'BROUILLON' && <div className="text-amber-700">Renvoyée : {v.validations.renvoi.motif}</div>}
                        {!v.validations && '—'}
                      </TableCell>
                      <TableCell className="max-w-xs text-xs">{v.notes ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        {v.document?.pdf && v.id !== version?.id && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => setVersionAffichee(v.id)}>
                            Afficher
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <Card data-testid="fiche-procedure">
            <CardHeader>
              <CardTitle>Fiche</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {p.description && <p>{p.description}</p>}
              <div>
                <span className="text-muted-foreground">Domaine</span>
                <div>{p.domaine.libelle}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Structure responsable</span>
                <div>
                  {p.structureResponsable.code} · {p.structureResponsable.libelle}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Responsable métier</span>
                <div>
                  {p.responsable.nom} · {p.responsable.fonction}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Applicabilité</span>
                <div className="flex flex-wrap gap-1 pt-1">
                  {data.cibles.map((c) => (
                    <Badge key={c.id} variant={c.type === 'TOUS' ? 'secondary' : 'outline'}>
                      {c.libelle}
                    </Badge>
                  ))}
                  {data.concernes !== null && <span className="text-xs text-muted-foreground">· {data.concernes} agent(s) concerné(s)</span>}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Importance</span>
                <div>
                  {p.confirmationRequise ? 'Confirmation explicite requise' : 'Lecture simple'}
                  {enVigueur?.echeanceDate ? ` · consultation attendue avant le ${dateFr(enVigueur.echeanceDate)}` : ''}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Téléchargement et impression</span>
                <div>
                  {p.telechargementAutorise ? 'téléchargement autorisé' : 'téléchargement non autorisé'} · {p.impressionAutorisee ? 'impression autorisée' : 'impression non autorisée'}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Circuit de publication</span>
                <div>Responsable métier → directeur de la structure responsable{p.visaDg ? ' → visa du Directeur Général' : ''} → publication</div>
              </div>
              {p.motsCles.length > 0 && (
                <div>
                  <span className="text-muted-foreground">Mots-clés</span>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {p.motsCles.map((m) => (
                      <Badge key={m} variant="outline">
                        {m}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          {data.personnel && (
            <Card data-testid="mon-statut-procedure">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  Mon statut <StatutConsultationBadge statut={data.personnel.statut as StatutConsultation} />
                </CardTitle>
                <CardDescription>Trace de consultation enregistrée sous votre compte ; le statut « Lu » atteste l'affichage du document, non sa lecture intégrale.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Première consultation</span>
                  <div>{data.personnel.premiereConsultation ? dateHeureFr(data.personnel.premiereConsultation) : 'jamais'}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Dernière consultation</span>
                  <div>{data.personnel.derniereConsultation ? `${dateHeureFr(data.personnel.derniereConsultation)} · ${data.personnel.nombre} ouverture(s) · ${duree(data.personnel.dureeCumulee)}` : '—'}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Échéance de consultation</span>
                  <div className={cn(data.personnel.enRetard && 'font-medium text-red-700')}>{data.personnel.echeance ? dateFr(data.personnel.echeance) : '—'}</div>
                </div>
                {p.confirmationRequise && (
                  <div>
                    <span className="text-muted-foreground">Confirmation de prise de connaissance</span>
                    <div>{data.personnel.confirmeeLe ? `confirmée le ${dateHeureFr(data.personnel.confirmeeLe)}` : data.actions.confirmer ? 'attendue : utilisez le bouton de confirmation' : 'attendue après la consultation dans le lecteur'}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={dialogue === 'decision'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{data.actions.valider?.etape === 'DG' ? 'Visa du Directeur Général' : 'Validation du directeur'} · version {enValidation?.numero}</DialogTitle>
            <DialogDescription>La validation finale publie la version et notifie les agents concernés ; un renvoi motivé la remet en brouillon chez le responsable métier.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="decision">Décision</Label>
              <SelectNatif id="decision" value={decision} onChange={(e) => setDecision(e.target.value as 'VALIDER' | 'RENVOYER')}>
                <option value="VALIDER">{data.actions.valider?.etape === 'DG' ? 'Viser et publier' : p.visaDg ? 'Valider (puis visa du DG)' : 'Valider et publier'}</option>
                <option value="RENVOYER">Renvoyer pour correction</option>
              </SelectNatif>
            </div>
            <div className="space-y-1">
              <Label htmlFor="commentaire">{decision === 'RENVOYER' ? 'Motif du renvoi (obligatoire)' : 'Commentaire'}</Label>
              <Textarea id="commentaire" value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogue(null)}>
              Annuler
            </Button>
            <Button type="button" disabled={actions.decider.isPending || (decision === 'RENVOYER' && !texte.trim())} onClick={() => data.actions.valider && executer(() => actions.decider.mutateAsync({ id: p.id, versionId: data.actions.valider!.versionId, decision, commentaire: texte.trim() || undefined }), decision === 'VALIDER' ? 'Décision enregistrée.' : 'Version renvoyée.')}>
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogue === 'version'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle version de {p.reference}</DialogTitle>
            <DialogDescription>La version en vigueur reste diffusée jusqu'à la publication de la nouvelle ; les agents concernés seront notifiés et devront la relire (et la confirmer si requis).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="nv-notes">Résumé des changements</Label>
              <Textarea id="nv-notes" value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nv-echeance">Échéance de consultation (jours après publication, vide : paramètre)</Label>
              <Input id="nv-echeance" type="number" min={1} max={365} value={echeance} onChange={(e) => setEcheance(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogue(null)}>
              Annuler
            </Button>
            <Button type="button" disabled={actions.nouvelleVersion.isPending} onClick={() => executer(() => actions.nouvelleVersion.mutateAsync({ id: p.id, notes: texte.trim() || null, echeanceConsultationJours: echeance ? Number(echeance) : null }), 'Version créée en brouillon : joignez le document puis soumettez.')}>
              Créer la version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogue === 'relancer'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Relancer les agents en défaut</DialogTitle>
            <DialogDescription>Notification PROC_RELANCE (avec courriel) aux agents concernés qui n'ont pas consulté la version en vigueur ou n'ont pas confirmé. Le suivi des consultations permet une relance ciblée.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="relance-message">Message (facultatif)</Label>
            <Textarea id="relance-message" value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogue(null)}>
              Annuler
            </Button>
            <Button type="button" disabled={actions.relancer.isPending} onClick={() => executer(async () => {
              const r = await actions.relancer.mutateAsync({ id: p.id, message: texte.trim() || undefined });
              toast.info(`${r.relances} agent(s) relancé(s).`);
            }, 'Relance envoyée.')}>
              Relancer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogue === 'archiver'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archiver {p.reference}</DialogTitle>
            <DialogDescription>La procédure et ses versions sont archivées : elle disparaît de la bibliothèque et des suivis ; les historiques de consultation sont conservés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="archive-motif">Motif</Label>
            <Textarea id="archive-motif" value={texte} onChange={(e) => setTexte(e.target.value)} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogue(null)}>
              Annuler
            </Button>
            <Button type="button" variant="destructive" disabled={actions.archiver.isPending} onClick={() => executer(() => actions.archiver.mutateAsync({ id: p.id, motif: texte.trim() || undefined }), 'Procédure archivée.')}>
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
